/**
 * RangeNode - Transforms pattern values to a specified output range
 *
 * Takes an input pattern array and maps all values from their detected
 * input range (min/max of the input) to a new output range.
 *
 * Example:
 *   Input:  [-1, 0, 1]  (detected range: -1 to 1)
 *   Params: min=0, max=100, round=true
 *   Output: [0, 50, 100]
 *
 * Endpoints:
 * - patternIn (input): incoming pattern data
 * - pattern (output): transformed pattern data
 * - min, max (input/output): output range
 * - round (input/output): whether to round to integers
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import {  params } from '@/nodes/TypedEndpoints';

const endpoints = params({
  patternIn: { order: 1, label: 'Input', dataType: 'array', isInput: true, allowInput: false, allowOutput: false },
  patternOut: { order: 2, label: 'Output', allowInput: false, allowOutput: false, dataType: 'array', isOutput: true },
  min: { value: 0, min: -1000, max: 1000, step: 1 },
  max: { value: 127, min: -1000, max: 1000, step: 1 },
  round: { dataType: 'boolean', value: true },
});

export class RangeNode extends BaseNode<typeof endpoints> {
  private inputPattern: number[] = [];

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'patternIn':
        this.handlePatternInput(payload.value);
        break;
      case 'min':
      case 'max':
      case 'round':
        // Param changed - re-transform
        this.transform();
        break;
    }
  }

  private handlePatternInput(value: number[]): void {
    this.inputPattern = value;
    this.transform();
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────



  // ─────────────────────────────────────────────────────────────
  // Private
  // ─────────────────────────────────────────────────────────────

  private transform(): void {
    const output = this.transformPattern(this.inputPattern);
    this.params.patternOut.set(output);
  }

  private transformPattern(input: number[]): number[] {
    if (input.length === 0) return [];

    // Find input range
    let inputMin = Math.min(...input)
    let inputMax = Math.max(...input)

    const shouldRound = this.params.round.value;

    return input.map(val => {
      let mapped = this.mapRange(val, inputMin, inputMax, this.params.min.value, this.params.max.value);
      if (shouldRound) {
        mapped = Math.round(mapped);
      }
      return mapped;
    });
  }

  private mapRange(
    value: number,
    inMin: number,
    inMax: number,
    outMin: number,
    outMax: number
  ): number {
    if (inMax === inMin) return outMin;
    return ((value - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin;
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const rangeNodeDef: NodeDef<RangeNode> = {
  type: 'Range',
  info: 'Maps input array values from their detected range to a new output range.',
  domain: Domain.Data,
  endpoints,
  ctor: RangeNode
};
